import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, Phone, Globe } from 'lucide-react';
import { company } from '../mock';

export const Header = ({ language, setLanguage }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const navItems = language === 'en'
    ? ['Tiers', 'Tours', 'Fleet', 'Pricing', 'Partners', 'Contact']
    : ['Tarifas', 'Tours', 'Frota', 'Preços', 'Parceiros', 'Contacto'];

  const navIds = ['tiers', 'tours', 'fleet', 'pricing', 'partners', 'contact'];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/80 backdrop-blur-xl backdrop-saturate-150 border-b border-[#E2E8F0] shadow-sm'
          : 'bg-white/40 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex items-center space-x-3" data-testid="logo-home-button">
            <div className="w-11 h-11 bg-gradient-to-br from-[#D4AF37] to-[#B48E2D] rounded-sm flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-lg heading-font tracking-tight">EG</span>
            </div>
            <div className="text-left">
              <h1 className="text-lg font-semibold text-[#0F172A] heading-font leading-none">{company.short}</h1>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#475569] mt-1">{company.tagline}</p>
            </div>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item, index) => (
              <button
                key={item}
                onClick={() => scrollToSection(navIds[index])}
                data-testid={`nav-${navIds[index]}`}
                className="text-sm text-[#475569] hover:text-[#D4AF37] font-medium transition-colors duration-200"
              >
                {item}
              </button>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="hidden md:flex items-center space-x-5">
            <button
              onClick={() => setLanguage(language === 'en' ? 'pt' : 'en')}
              data-testid="language-toggle"
              className="flex items-center space-x-1 text-[#475569] hover:text-[#D4AF37] transition-colors"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">{language === 'en' ? 'PT' : 'EN'}</span>
            </button>
            <a href={`tel:${company.phoneRaw}`} data-testid="header-phone" className="hidden lg:flex items-center space-x-1 text-[#475569] hover:text-[#D4AF37] transition-colors">
              <Phone className="w-4 h-4" />
              <span className="text-sm">{company.phone}</span>
            </a>
            <Button
              onClick={() => scrollToSection('booking')}
              data-testid="nav-book-button"
              className="bg-[#D4AF37] hover:bg-[#B48E2D] text-white rounded-sm px-6 transition-colors shadow-sm"
            >
              {language === 'en' ? 'Book Now' : 'Reservar'}
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className="md:hidden text-[#0F172A] hover:text-[#D4AF37] transition-colors"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 py-4 border-t border-[#E2E8F0]">
            <nav className="flex flex-col space-y-3">
              {navItems.map((item, index) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(navIds[index])}
                  data-testid={`mobile-nav-${navIds[index]}`}
                  className="text-left text-[#475569] hover:text-[#D4AF37] font-medium transition-colors py-2"
                >
                  {item}
                </button>
              ))}
              <div className="pt-3 border-t border-[#E2E8F0] space-y-3">
                <button
                  onClick={() => setLanguage(language === 'en' ? 'pt' : 'en')}
                  className="flex items-center space-x-2 text-[#475569] hover:text-[#D4AF37] transition-colors"
                >
                  <Globe className="w-4 h-4" />
                  <span>{language === 'en' ? 'Português' : 'English'}</span>
                </button>
                <a href={`tel:${company.phoneRaw}`} className="flex items-center space-x-2 text-[#475569] hover:text-[#D4AF37] transition-colors">
                  <Phone className="w-4 h-4" />
                  <span>{company.phone}</span>
                </a>
                <Button
                  onClick={() => scrollToSection('booking')}
                  className="w-full bg-[#D4AF37] hover:bg-[#B48E2D] text-white rounded-sm"
                >
                  {language === 'en' ? 'Book Now' : 'Reservar'}
                </Button>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
